<template>
  <div ref="containerRef" class="w-12">
    <div
      v-if="imageLoaded"
      class="img-render-nearest"
      :style="{
        width: finalWidth + 'px',
        height: finalHeight + 'px',
        backgroundImage: `url(${url})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${scaledImageWidth}px ${scaledImageHeight}px`,
        backgroundPosition: `-${scaledFrameOffset}px 0px`,
      }"
    />
    <div v-else class="loading loading-ring loading-lg" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from "vue";

// Props 定义
const props = withDefaults(
  defineProps<{
    url: string;
    index: number;
    splitWidth?: number;
  }>(),
  {
    splitWidth: 48,
  }
);

const emit = defineEmits<{
  (e: "loaded", totalFrames: number): void;
}>();

// 响应式变量
const imageWidth = ref(0);
const imageHeight = ref(0);
const imageLoaded = ref(false);
const containerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(0);

// 获取容器实际宽度
const updateContainerWidth = () => {
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect();
    if (rect.width > 0) {
      containerWidth.value = rect.width;
    }
  }
};

// 计算缩放比例
const scaleRatio = computed(() => {
  if (!imageLoaded.value || containerWidth.value === 0) {
    return 1;
  }
  return containerWidth.value / props.splitWidth;
});

// 计算最终显示尺寸
const finalWidth = computed(() => {
  if (!imageLoaded.value || containerWidth.value === 0) {
    return props.splitWidth;
  }
  return containerWidth.value;
});

const finalHeight = computed(() => {
  if (!imageLoaded.value) {
    return 48; // 默认高度
  }
  return Math.round(imageHeight.value * scaleRatio.value);
});

// 计算缩放后的背景图片尺寸
const scaledImageWidth = computed(() => {
  return Math.round(imageWidth.value * scaleRatio.value);
});

const scaledImageHeight = computed(() => {
  return Math.round(imageHeight.value * scaleRatio.value);
});

// 计算缩放后的帧偏移量
const scaledFrameOffset = computed(() => {
  return Math.round(props.index * props.splitWidth * scaleRatio.value);
});

// 加载图片并获取尺寸
const loadImage = () => {
  if (!props.url) return;

  imageLoaded.value = false;
  const img = new Image();

  img.onload = () => {
    imageWidth.value = img.width;
    imageHeight.value = img.height;
    if (imageLoaded.value) return;
    imageLoaded.value = true;
    emit("loaded", Math.floor(img.width / props.splitWidth));

    // 图片加载完成后更新容器宽度
    nextTick(() => {
      updateContainerWidth();
    });
  };

  img.onerror = () => {
    console.error("SpriteImgList - 图片加载失败:", props.url);
    imageLoaded.value = false;
  };

  img.src = props.url;
};

// 监听 URL 变化重新加载图片
watch(
  () => props.url,
  () => {
    loadImage();
  },
  { immediate: true }
);

// 使用 ResizeObserver 监听容器尺寸变化
let resizeObserver: ResizeObserver | null = null;

// 组件挂载时加载图片并设置观察器
onMounted(() => {
  // 设置 ResizeObserver 监听容器尺寸变化
  if (containerRef.value && window.ResizeObserver) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          containerWidth.value = entry.contentRect.width;
        }
      }
    });
    resizeObserver.observe(containerRef.value);
  }

  // 初始获取容器宽度
  nextTick(() => {
    updateContainerWidth();
  });
});

// 组件卸载时清理观察器
onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// 暴露最终尺寸给父组件使用
defineExpose({
  finalWidth,
  finalHeight,
  imageLoaded,
  scaleRatio,
  containerWidth,
});
</script>
